import { DialogActions, FormControl, FormHelperText, Grid, makeStyles, MenuItem, Select, TextField } from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { Controller, useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { EDIT_FOLDER_FORM } from "../../data/StringConstants";
import CustomButton from "../CustomButton/CustomButton";

const useStyles = makeStyles(theme => ({
    root: {
        "& .MuiFormControl-root": {
            margin: theme.spacing(4, 0),
        }
    }
}));

/**
 * A component which represents the form used when editing a Folder's details.
 * @param {Object} data The form data
 * @param {{id: number, name: string}[]} data.brandingSets An array of objects containing the branding sets associated with the Folder. Each object contains the branding set id and name.
 * @param {string} data.name The name of the folder being edited.
 * @param {string} data.description The description of the folder being edited. Optional.
 * @param {number} data.defaultBrandingSetId The ID of the currently selected branding set.
 * @param {number} data.folderId The ID of the Folder being edited.
 * @param {boolean} isCreate True if the form is being used to create a folder. False if we are editing
 */
export default function EditFolderForm({ onSubmit, onCancel, data, isCreate }) {
    const classes = useStyles();
    let confirmButtonText = isCreate ? EDIT_FOLDER_FORM.CREATE_SAVE_BTN_TEXT : EDIT_FOLDER_FORM.SAVE_BTN_TEXT;
    let confirmButtonColor = isCreate ? "green" : "primary";

    const { brandingSets, defaultBrandingSetId, name, description, folderId } = data;

    const defaultValues = {
        name: name,
        description: description,
        brandingSet: defaultBrandingSetId,
        folderId: folderId
    };

    const { control, register, handleSubmit, errors } = useForm({ defaultValues });

    return (
        <Grid container>
            <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" name="folderId" ref={register} />
                <FormControl fullWidth>
                    <TextField
                        inputRef={register({ required: true, minLength: 1, maxLength: 100 })}
                        label={EDIT_FOLDER_FORM.FOLDER_NAME_LABEL}
                        name="name"
                        required
                    />
                    <FormHelperText error>
                        {errors.name &&
                            errors.name.type === "maxLength" && (EDIT_FOLDER_FORM.NAME_FIELD_ERROR_MSG)}
                    </FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                    <TextField
                        inputRef={register({ maxLength: 1000 })}
                        label={EDIT_FOLDER_FORM.FOLDER_DESCRIPTION_LABEL}
                        name="description"
                        multiline
                        rowsMax={12}
                    />
                    <FormHelperText error>
                        {errors.description &&
                            errors.description.type === "maxLength" && (EDIT_FOLDER_FORM.DESCRIPTION_FIELD_ERROR_MSG)}
                    </FormHelperText>
                </FormControl>

                <FormControl fullWidth>
                    <FormHelperText>{EDIT_FOLDER_FORM.BRANDING_SET_LABEL}</FormHelperText>
                    <Controller
                        name="brandingSet"
                        control={control}
                        label={EDIT_FOLDER_FORM.BRANDING_SET_LABEL}
                        as={
                            <Select labelId="branding-set">
                                {brandingSets.map(option => (
                                    <MenuItem
                                        key={option.id}
                                        value={option.id}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                    />
                    <FormHelperText>{EDIT_FOLDER_FORM.BRANDING_SET_HELPER}</FormHelperText>
                </FormControl>

                <DialogActions>
                    <CustomButton onClick={onCancel}>
                        {EDIT_FOLDER_FORM.CANCEL_BTN_TEXT}
                    </CustomButton>
                    <CustomButton color={confirmButtonColor} variant="contained" type="submit">
                        {confirmButtonText}
                    </CustomButton>
                </DialogActions>

            </form>
        </Grid>
    );
}

EditFolderForm.propTypes = {
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    data: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        brandingSets: PropTypes.array,
        defaultBrandingSetId: PropTypes.number
    })
};